import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { usePortfolioBuilder } from "./PortfolioBuilderContext";
import FilterBuilderBrowser from "./FilterBuilderBrowser";
import BuilderCanvas from "./BuilderCanvas";
import type { FilterDraft } from "../hooks/useFilterBuilder";
import type { SavedWatchlist, EditableWatchlist } from "../listTypes";
import { applyPrebuiltOverrides } from "../prebuiltWatchlists";
import { formatListName } from "@/shared/utils/formatListName";
import { savedViewColor } from "@/shared/utils/savedViewHighlight";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import useAuth from "@/hooks/useAuth";

/**
 * The Watchlists page: the builder, with your lists and the Newsquawk library
 * under its two start cards.
 *
 * There used to be a second view here, a three-pane library browser behind a
 * "Browse library" button. Once both lists sat on the builder's own landing —
 * searchable, in the widget menus' rows, opening into the builder — it
 * answered nothing the landing didn't, and was a second way of showing the
 * same lists with a different interaction model.
 */
export default function PortfolioBuilderBrowser() {
  const { authenticated } = useAuth();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const {
    setPreviewWatchlist,
    setPreviewFilter,
    setPreviewTagWatchlist,
    isPreviewingWatchlist,
    isPreviewingFilter,
  } = usePortfolioBuilder();

  // The builder: every new list, every copy, and every list to edit.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<FilterDraft | null>(null);
  const [editingFilter, setEditingFilter] = useState<CategoryQuery | null>(null);
  const [editingWatchlistId, setEditingWatchlistId] = useState<number | null>(null);
  const [editingTagWatchlistId, setEditingTagWatchlistId] = useState<number | null>(null);

  /**
   * Open the one builder: a new list, a draft, or a list of any kind.
   *
   * Typed terms, tags and lists are built in the same place, and what the
   * list holds decides how it is stored (listStorage.ts). Before, a watchlist,
   * a list of tags and a combination were each built on their own surface,
   * and the only way to a combination from scratch was a link that appeared
   * after ticking lists.
   */
  const openBuilder = useCallback(
    (
      target: {
        filter?: CategoryQuery | null;
        watchlistId?: number | null;
        tagWatchlistId?: number | null;
        draft?: FilterDraft | null;
      } = {}
    ) => {
      setEditingFilter(target.filter ?? null);
      setEditingWatchlistId(target.watchlistId ?? null);
      setEditingTagWatchlistId(target.tagWatchlistId ?? null);
      setFilterDraft(target.draft ?? null);
      setBuilderOpen(true);
    },
    []
  );

  // The concierge panel creates resources itself (one reviewed step); this is
  // only the power-user hand-off, where the filter's watchlists have already
  // been created and resolved to real ids.
  const handleOpenFilterInBuilder = (draft: FilterDraft) => openBuilder({ draft });

  // Deep link: /watchlists-filters?create=ticker|keyword|tag|advanced opens
  // the builder (used by widget filter panels' "create new" links and the
  // onboarding checklist). Param is consumed and removed.
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Deep link into a combined watchlist, e.g. /watchlists-filters?editFilter=12
   *
   * The filter panel can apply one combined watchlist but cannot build one, so
   * its explanation of that limit needs somewhere to send people. Landing on
   * the page and asking them to find the list again is the kind of half-answer
   * that makes a limit feel like a fault.
   *
   * Consumed once and stripped from the URL, so a refresh or a back-navigation
   * doesn't reopen an editor the user has since closed.
   */
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const wanted = searchParams.get("editFilter");
    if (!wanted) return;
    const filter = (queriesQuery.data || []).find(
      (q) => String(q.id) === wanted
    );
    // Wait for the queries to load rather than giving up on the first render.
    if (!filter) return;
    deepLinkHandled.current = true;
    openBuilder({ filter });
    searchParams.delete("editFilter");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, queriesQuery.data, openBuilder]);

  useEffect(() => {
    const create = searchParams.get("create");
    if (!create) return;
    // The type in these links is legacy: there is one builder, and what the
    // list holds decides what it is — tags included.
    if (["ticker", "keyword", "tag", "advanced"].includes(create)) openBuilder();
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume the param once per change
  }, [searchParams]);

  /**
   * After a save, show the thing that was just made.
   *
   * Saving used to navigate to the library — answering a question the user
   * hadn't asked and hiding the one they had ("did that work?"). Staying on
   * the builder and previewing the saved list puts the answer in the headline
   * strip underneath, and the list itself at the top of "Recently created".
   *
   * The setPreview* calls toggle, so each is skipped when that list is already
   * showing — otherwise saving a list twice in a row turned its preview off.
   */
  const handleSaved = useCallback(
    (saved?: SavedWatchlist) => {
      if (!saved) return;
      if (saved.kind === "watchlist") {
        if (!isPreviewingWatchlist(saved.id)) setPreviewWatchlist(saved.id);
      } else if (saved.kind === "combined") {
        if (!isPreviewingFilter(saved.id)) setPreviewFilter(saved.id);
      } else {
        // Only the desk's in-place edit of a Newsquawk one saves a tag
        // watchlist now. Always set: the tags may have changed even if the
        // list hasn't.
        const tw = (watchlistsQuery.data ?? []).find((w) => w.id === saved.id);
        setPreviewTagWatchlist(saved.id, saved.tags ?? tw?.tags ?? []);
      }
    },
    [
      setPreviewWatchlist,
      setPreviewFilter,
      setPreviewTagWatchlist,
      isPreviewingWatchlist,
      isPreviewingFilter,
      watchlistsQuery.data,
    ]
  );

  /**
   * A library list opened in the builder as a copy.
   *
   * Opened as the list it is — its terms or tags, and the colour the user
   * currently sees it in — with nothing saved until they save. What that save
   * does depends on what they changed (see libraryChanges): the colour alone
   * is kept as their own override of the library list; a
   * name, terms, tags or alerts make a new list of their own. A copy is how a
   * customer changes a library list's terms, since the list itself is the
   * desk's. The copy is the same one the concierge's "Build on it" makes.
   */
  const handleBuildOn = useCallback((item: EditableWatchlist) => {
    if (item.kind === "watchlist") {
      // The rows carry the stored record; the user sees it in their own
      // colour, if they have set one.
      const [p] = applyPrebuiltOverrides([item.portfolio]);
      openBuilder({ draft: {
        name: formatListName(p.name),
        tickers: p.tickers,
        keywords: p.keywords ?? [],
        basedOn: formatListName(p.name),
        libraryId: p.id,
        libraryKind: "watchlist",
        color: p.color,
      } });
    } else if (item.kind === "tag") {
      // No colour of its own to keep, so any change makes a list of the
      // user's own — combined, since it holds tags.
      openBuilder({ draft: {
        name: formatListName(item.watchlist.name),
        tags: item.watchlist.tags,
        basedOn: formatListName(item.watchlist.name),
      } });
    } else {
      const q = item.query;
      openBuilder({ draft: {
        name: formatListName(q.queryName),
        searchKeyword: q.searchKeyword || undefined,
        tags: q.categories,
        portfolioIds: q.portfolioIds ?? [],
        tagsWatchlistIds: q.tagsWatchlistIds ?? [],
        filterMode: q.filterMode,
        keywordOp: q.keywordOp,
        keywordWatchlistOp: q.keywordWatchlistOp,
        basedOn: formatListName(q.queryName),
        libraryId: q.id,
        libraryKind: "combined",
        // The rows carry the query with the user's own colour applied; with
        // none stored, it highlights in the colour derived from its id.
        color: savedViewColor(q),
      } });
    }
  }, [openBuilder]);

  /**
   * One of the user's own lists, or a Newsquawk one the desk is editing,
   * opened in the builder whatever store it lives in. The builder decides
   * whether it can change kind: a customer's tag watchlist becomes a combined
   * one on save, a Newsquawk list stays what it is (see inPlace in
   * useFilterBuilder).
   */
  const handleEdit = useCallback(
    (item: EditableWatchlist) => {
      if (item.kind === "watchlist") openBuilder({ watchlistId: item.portfolio.id });
      else if (item.kind === "tag") openBuilder({ tagWatchlistId: item.watchlist.id });
      else openBuilder({ filter: item.query });
    },
    [openBuilder]
  );

  return (
    <>
      {/* The landing — the two ways in, your lists and the library — and the
          concierge. Every list itself is built in the builder below.

          As tall as what it holds, up to two thirds of the page, and the
          headline strip below gets the rest; the concierge (data-building)
          keeps a fixed two thirds so the strip doesn't jump about as it
          works. Signed in, the strip is there at every width; logged out, on
          wide screens only — hence the cap's breakpoint. */}
      <div
        className={clsx(
          "flex min-h-0 flex-[0_1_auto] flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-gray-800 has-[>[data-building]]:flex-[2_1_0%]",
          authenticated ? "max-h-[66%]" : "xl:max-h-[66%]"
        )}
      >
        <div className="flex items-center gap-3 border-b border-gray-700/50 bg-gray-800 px-4 py-2.5">
          <h2 className="flex-1 text-sm font-semibold text-gray-100">
            Build a watchlist
          </h2>
        </div>
        <BuilderCanvas
          onOpenInBuilder={handleOpenFilterInBuilder}
          onBuildManually={() => openBuilder()}
          // The concierge's single watchlist, typed into the builder. Logged
          // out this is the guided demo, and the builder says nothing is
          // saved.
          onOpenWatchlist={(draft) =>
            openBuilder({
              draft: {
                name: draft.name,
                tickers: draft.tickers,
                keywords: draft.keywords,
                tags: draft.tags,
              },
            })
          }
          onBuildOn={handleBuildOn}
          onEditWatchlist={handleEdit}
        />
      </div>

      <FilterBuilderBrowser
        isOpen={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setEditingFilter(null);
          setEditingWatchlistId(null);
          setEditingTagWatchlistId(null);
          setFilterDraft(null);
        }}
        queryId={editingFilter?.id}
        portfolioId={editingWatchlistId}
        tagWatchlistId={editingTagWatchlistId}
        draft={filterDraft}
        // Saving in here is a save like any other: the modal closes itself
        // through onClose, and this is what tells the page what was made.
        onSaved={handleSaved}
      />
    </>
  );
}
